import { Flex } from "../../components/Flex";
import { SelectItem } from "../types/selectFieldInternalTypes";
import Icon from "../../components/Icon";
import { Avatar } from "../../components/Avatar";
import { Chip } from "../../components/Chip";
import styles from "./SelectOption.module.scss";
import CheckboxOutline from "@servicetitan/hammer-icon/mdi/round/check_box_outline_blank.svg";
import CheckboxChecked from "@servicetitan/hammer-icon/mdi/round/check_box.svg";
import CheckboxIndeterminate from "@servicetitan/hammer-icon/mdi/round/indeterminate_check_box.svg";
import Check from "@servicetitan/hammer-icon/mdi/round/check.svg";
import cx from "classnames";
import { useMemo } from "react";
import Text from "../../components/Text";
import { CheckState } from "../../types";
import Spinner from "../../components/Spinner";
import { GetItemPropsFn } from "../types/selectFieldInternalTypes";

export type SelectOptionProps = {
  item: SelectItem;
  index: number;
  highlighted: boolean;
  disabled: boolean;
  getItemProps: GetItemPropsFn;
  selectionType: "single" | "multiple";
  checked: CheckState;
  isListScrolled?: boolean;
};

export const SelectOption = ({
  item,
  index,
  highlighted,
  disabled,
  getItemProps,
  selectionType,
  checked,
  isListScrolled = false,
}: SelectOptionProps) => {
  const isBulkAction =
    item.type === "select-all" || item.type === "select-filtered";
  const checkbox = useMemo(() => {
    if (checked === "checked")
      return { icon: CheckboxChecked, class: styles["checked"] };
    if (checked === "unchecked")
      return { icon: CheckboxOutline, class: styles["unchecked"] };
    if (checked === "indeterminate")
      return { icon: CheckboxIndeterminate, class: styles["indeterminate"] };
    return { icon: CheckboxOutline, class: styles["unchecked"] };
  }, [checked]);

  const content = item.original.content;
  const hasAvatar = !!content?.avatar;
  const hasChips = !!content?.chips?.length;
  const hasIcon = !!content?.icon;

  return (
    <div
      key={item.id}
      data-item-index={index}
      {...getItemProps({
        item,
        index,
        "data-type": item.type,
        className: cx(styles["select-field-list-item"], {
          [styles.highlighted]: highlighted,
          [styles.selected]: checked === "checked",
          [styles.disabled]: disabled,
          [styles.single]: selectionType === "single",
          [styles["bulk-action"]]: isBulkAction,
          [styles.floating]: isBulkAction && isListScrolled,
        }),
      })}
    >
      {selectionType === "multiple" && (
        <div className={styles["side-content"]}>
          {checked === "loading" ? (
            <Flex
              alignItems="center"
              justifyContent="center"
              style={{ height: "1.5rem", width: "1.5rem" }}
            >
              <Spinner size="small" />
            </Flex>
          ) : (
            // Decorative: selection state is conveyed by aria-selected on the <li>
            <Icon
              aria-hidden="true"
              size="large"
              svg={checkbox.icon}
              className={cx(styles["checkbox"], checkbox.class)}
            />
          )}
        </div>
      )}
      {hasAvatar && (
        // aria-hidden: Avatar has internal <SrOnly> text that would duplicate the option label
        <div className={styles["side-content"]} aria-hidden="true">
          <Avatar size="medium" {...content!.avatar!} />
        </div>
      )}
      <Flex grow={1} shrink={1} direction="column">
        <Text size="medium">{content?.title ?? item.original.label}</Text>
        {content?.description && (
          <Text size="small" className={styles["description"]}>
            {content.description}
          </Text>
        )}
        {hasChips && (
          // Group label gives screen readers context that these are tags
          <Flex
            wrap="wrap"
            gap="1"
            className={styles["chips-row"]}
            role="group"
            aria-label="Tags"
          >
            {content!.chips!.map((chip, i) => (
              <Chip key={`${chip.label}-${i}`} size="small" {...chip} />
            ))}
          </Flex>
        )}
      </Flex>
      {hasIcon && (
        <div className={styles["side-content"]}>
          {/* Treated as decorative unless consumer provides a label */}
          <Icon
            size="large"
            svg={content!.icon!.svg}
            color={content!.icon!.color}
            aria-hidden={content!.icon!.label ? undefined : "true"}
            aria-label={content!.icon!.label}
          />
        </div>
      )}
      {selectionType === "single" && checked === "checked" && (
        <div className={styles["side-content"]}>
          {/* Decorative: selection state is conveyed by aria-selected on the <li>, hence aria-hidden */}
          <Icon
            aria-hidden="true"
            size="medium"
            svg={Check}
            className={cx(styles["checkbox"], styles["checked"])}
          />
        </div>
      )}
    </div>
  );
};
